import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { scale } from '@components/frame';
import { TimeTileConfig } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { TileFonts } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/types';
import { selectIsWithERP } from '@reducers/membership';
import { withERP } from '@helpers';
import { TimeTilesProps } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles';
import { TimeCategory } from '@interfaces/base';
import { TILE_COLORS } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/constants';

interface UseManageTimeTilesConfigParams extends Pick<TimeTilesProps, 'eventTimelineData'> {}

const tileFonts: TileFonts = {
  title: 'Subhead 1',
  description: 'Caption 2',
  time: { fontForDigit: 'Time 1 bold', fontForSecondDigit: 'Time 3 bold', fontForDelimiter: 'Subhead 1 bold' },
} as const;

const useManageTimeTilesConfig = ({ eventTimelineData }: UseManageTimeTilesConfigParams) => {
  const isWithERP = useSelector(selectIsWithERP);

  const timeTilesConfig: TimeTileConfig[] = useMemo(
    () => [
      {
        titleConfig: {
          color: TILE_COLORS[TimeCategory.IDEAL],
          fontType: tileFonts.title,
          text: 'Ideal',
        },
        iconConfig: {
          icon: 'runningBunny',
          width: scale(20),
          height: scale(24),
          color: TILE_COLORS[TimeCategory.IDEAL],
        },
        timeConfig: {
          timeValue: eventTimelineData?.idealTimeInSec ?? 0,
          color: TILE_COLORS[TimeCategory.IDEAL],
          ...tileFonts.time,
        },
        descriptionConfig: {
          color: TILE_COLORS[TimeCategory.IDEAL],
          fontType: tileFonts.description,
          text: 'Set manually',
        },
        timeCategory: TimeCategory.IDEAL,
        editable: true,
      },
      {
        titleConfig: {
          color: TILE_COLORS[TimeCategory.PAID],
          fontType: tileFonts.title,
          text: 'Paid',
        },
        iconConfig: {
          icon: 'euro',
          width: scale(18),
          height: scale(24),
          color: TILE_COLORS[TimeCategory.PAID],
        },
        timeConfig: {
          timeValue: eventTimelineData?.paidTimeInSec ?? 0,
          color: TILE_COLORS[TimeCategory.PAID],
          ...tileFonts.time,
        },
        descriptionConfig: {
          color: TILE_COLORS[TimeCategory.PAID],
          fontType: tileFonts.description,
          text: 'From ERP job',
        },
        timeCategory: TimeCategory.PAID,
        editable: !withERP(isWithERP),
      },
      {
        titleConfig: {
          color: TILE_COLORS[TimeCategory.ACTUAL],
          fontType: tileFonts.title,
          text: 'Average',
        },
        iconConfig: {
          icon: 'actual',
          width: scale(18),
          height: scale(19),
          color: TILE_COLORS[TimeCategory.ACTUAL],
        },
        timeConfig: {
          timeValue: eventTimelineData?.actualTimeInSec ?? 0,
          color: TILE_COLORS[TimeCategory.ACTUAL],
          ...tileFonts.time,
        },
        descriptionConfig: {
          color: TILE_COLORS[TimeCategory.ACTUAL],
          fontType: tileFonts.description,
          text: 'Updates automatically',
        },
        timeCategory: TimeCategory.ACTUAL,
        editable: false,
      },
      {
        titleConfig: {
          color: TILE_COLORS[TimeCategory.SCHEDULED],
          fontType: tileFonts.title,
          text: 'Scheduled',
        },
        iconConfig: {
          icon: 'scheduledTime',
          width: scale(20),
          height: scale(20),
          color: TILE_COLORS[TimeCategory.SCHEDULED],
        },
        timeConfig: {
          timeValue: eventTimelineData?.scheduledTimeInSec ?? 0,
          color: TILE_COLORS[TimeCategory.SCHEDULED],
          ...tileFonts.time,
        },
        descriptionConfig: {
          color: TILE_COLORS[TimeCategory.SCHEDULED],
          fontType: tileFonts.description,
          text: 'Updates automatically',
        },
        timeCategory: TimeCategory.SCHEDULED,
        editable: true,
      },
    ],
    [eventTimelineData?.idealTimeInSec, eventTimelineData?.paidTimeInSec, eventTimelineData?.actualTimeInSec, eventTimelineData?.scheduledTimeInSec, isWithERP]
  );

  const sortedTimeTilesConfig = useMemo(
    () => [...timeTilesConfig].sort((a, b) => a.timeConfig.timeValue - b.timeConfig.timeValue),
    [timeTilesConfig]
  );

  return {
    timeTilesConfig: sortedTimeTilesConfig,
  };
};

export { useManageTimeTilesConfig };
